// src/app/research-themes/error.tsx - Updated with modular color system
'use client';

import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';

import { withOpacity } from '@/lib/colors';
import { useColorTheme } from '@/components/ColorModeProvider';

interface ErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function Error({ error, reset }: ErrorProps) {
  const colors = useColorTheme();

  React.useEffect(() => {
    console.error('Research Themes page error:', error);
  }, [error]);

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      background: colors.background.default, 
      display: 'flex', 
      alignItems: 'center' 
    }}>
      <Container maxWidth="md">
        <Paper
          sx={{
            p: 6,
            textAlign: 'center',
            background: colors.gradients.card,
            border: `1px solid ${colors.border.primary}`,
          }}
        >
          <Typography 
            variant="h3" 
            sx={{ 
              color: colors.brand.accent.dark, 
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: '1.8rem', md: '2.5rem' }
            }}
          >
            Something went wrong
          </Typography>
          
          <Typography 
            variant="h6" 
            sx={{ 
              color: colors.text.secondary, 
              mb: 2,
              fontWeight: 500
            }}
          >
            Unable to load Research Themes
          </Typography>
          
          <Typography 
            variant="body1" 
            sx={{ 
              color: colors.text.disabled, 
              mb: 4,
              maxWidth: '500px',
              mx: 'auto',
              lineHeight: 1.6
            }}
          >
            We encountered an issue while loading the research themes. This might be a temporary 
            problem with our servers. Please try refreshing the page.
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              onClick={reset}
              startIcon={<RefreshIcon />}
              sx={{
                background: colors.gradients.primary,
                color: colors.brand.accent.contrastText,
                fontWeight: 600,
                px: 3,
                py: 1.5,
                '&:hover': {
                  background: colors.gradients.primary,
                  filter: 'brightness(1.1)',
                }
              }}
            >
              Try Again
            </Button>
            
            <Button
              variant="outlined"
              onClick={() => window.location.href = '/'}
              sx={{
                color: colors.brand.accent.dark,
                borderColor: colors.brand.accent.main,
                px: 3,
                py: 1.5,
                '&:hover': {
                  background: withOpacity(colors.brand.accent.main, 0.1),
                  borderColor: colors.brand.accent.main,
                }
              }}
            >
              Go Home
            </Button>
          </Box>
          
          {process.env.NODE_ENV === 'development' && (
            <Box sx={{ 
              mt: 4, 
              p: 2, 
              background: withOpacity('#ff0000', 0.1), 
              borderRadius: 1,
              border: `1px solid ${withOpacity('#ff6b6b', 0.3)}`,
            }}>
              <Typography variant="caption" sx={{ 
                color: '#ff6b6b', 
                fontFamily: 'monospace',
                display: 'block',
                textAlign: 'left',
                wordBreak: 'break-word',
              }}>
                <strong>Development Error:</strong><br />
                {error.message}
                {error.digest && (
                  <>
                    <br />
                    <strong>Digest:</strong> {error.digest}
                  </>
                )}
              </Typography>
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
}